import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ThemeProvider as MaterialThemeProvider, THEME_ID as MATERIAL_THEME_ID, createTheme } from '@mui/material/styles'
import { CssVarsProvider as JoyCssVarsProvider, useColorScheme as useJoyColorScheme } from '@mui/joy/styles'
import { Chart as ChartJS } from 'chart.js'

export type ThemeMode = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'laundry-theme'

type ThemeContextValue = {
  theme: ThemeMode
  setTheme: (mode: ThemeMode) => void
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

export const readStoredTheme = (): ThemeMode => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    // storage unavailable (private mode, blocked) – fall through to system preference
  }
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark'
  }
  return 'light'
}

const applyThemeToDocument = (mode: ThemeMode) => {
  const root = document.documentElement
  root.setAttribute('data-theme', mode)
  // Bootstrap 5.3 reads this attribute for its own dark palette (modals, forms, ...)
  root.setAttribute('data-bs-theme', mode)
  // Tailwind `dark:` variants
  root.classList.toggle('dark', mode === 'dark')
  // Native controls (date inputs, scrollbars) follow the scheme
  root.style.colorScheme = mode

  // Chart.js has no CSS, so recolor axis labels and grid lines explicitly
  const dark = mode === 'dark'
  ChartJS.defaults.color = dark ? '#a6adbf' : '#64748B'
  ChartJS.defaults.borderColor = dark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.1)'
  Object.values(ChartJS.instances).forEach((chart) => chart.update())
}

/** Keeps MUI Joy's own color scheme in step with the app theme. */
const JoyModeSync = ({ mode }: { mode: ThemeMode }) => {
  const { setMode } = useJoyColorScheme()
  useEffect(() => {
    setMode(mode)
  }, [mode, setMode])
  return null
}

export const AppThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setThemeState] = useState<ThemeMode>(readStoredTheme)

  useEffect(() => {
    applyThemeToDocument(theme)
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme)
    } catch {
      // ignore – theme still applies for this page load
    }
  }, [theme])

  // Follow the OS setting until the user picks a theme explicitly
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (!media) return
    const onChange = (e: MediaQueryListEvent) => {
      let stored: string | null = null
      try { stored = window.localStorage.getItem(THEME_STORAGE_KEY) } catch { /* noop */ }
      if (stored !== 'light' && stored !== 'dark') {
        setThemeState(e.matches ? 'dark' : 'light')
      }
    }
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  const setTheme = useCallback((mode: ThemeMode) => setThemeState(mode), [])
  const toggleTheme = useCallback(() => setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark')), [])

  // Kumesa's palette (kumesa/lib/core/theme.dart), mirrored for MUI components
  const materialTheme = useMemo(() => createTheme({
    palette: {
      mode: theme,
      primary: { main: '#0891B2', dark: '#0E7490', light: '#22D3EE', contrastText: '#ffffff' },
      secondary: { main: '#0F172A' },
      warning: { main: '#F59E0B' },
      error: { main: '#991B1B' },
      ...(theme === 'dark'
        ? {
            background: { default: '#0f1117', paper: '#181b23' },
            text: { primary: '#e6e8ef', secondary: '#a6adbf' },
            divider: '#2e3342',
          }
        : {
            background: { default: '#F8FAFC', paper: '#ffffff' },
            text: { primary: '#0F172A', secondary: '#64748B' },
            divider: '#E2E8F0',
          }),
    },
    typography: {
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    },
    shape: { borderRadius: 12 },
    components: {
      MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } } },
      MuiToggleButton: { styleOverrides: { root: { textTransform: 'none' } } },
    },
  }), [theme])

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme])

  return (
    <ThemeContext.Provider value={value}>
      <MaterialThemeProvider theme={{ [MATERIAL_THEME_ID]: materialTheme }}>
        <JoyCssVarsProvider modeStorageKey="laundry-joy-mode" defaultMode={theme}>
          <JoyModeSync mode={theme} />
          {children}
        </JoyCssVarsProvider>
      </MaterialThemeProvider>
    </ThemeContext.Provider>
  )
}

export const useAppTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    throw new Error('useAppTheme must be used inside <AppThemeProvider>')
  }
  return ctx
}
